import React, {useState} from 'react';
import {
  Box,
  Center,
  Divider,
  Flex,
  HStack,
  VStack,
  useTheme,
  useDisclosure,
} from '@chakra-ui/react';
import {TextG12, TextG14} from '../../../components/typography';
import useAuth from '../../../hooks/useAuth';
import UserAvatar from '../../../components/UserAvatar';
import Icon from '../../../components/Icon';
import {useGetUserAddresses} from '../../../hooks/useUserAddress';
import {useNavigate} from 'react-router-dom';
import ProfilePhotoModal from '../../Profile/Edit/ProfilePhotoModal';

const ProfileCard = ({
  onNicknameClick,
  onAddressClick,
}: {
  onNicknameClick: () => void;
  onAddressClick: () => void;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {user, reFetchUserData} = useAuth();
  const {userAddresses} = useGetUserAddresses({
    userId: user?.id,
    addressType: 'HOME',
  });
  const {isOpen: isPhotoOpen, onOpen: onPhotoOpen, onClose: onPhotoClose} = useDisclosure();

  const address = userAddresses?.[0];
  const cityName =
    address?.city && typeof address.city === 'object' ? (address.city as any).name : address?.city;
  const displayAddress = address
    ? `${address.street_address || ''}${cityName ? ', ' + cityName : ''}`
        .replace(/^,\s*/, '')
        .trim()
    : '';
  const displayName = `${user?.name || ''} ${user?.surname || ''}`.trim();
  const initials = `${user?.name?.charAt(0) || ''}${user?.surname?.charAt(0) || ''}`;

  return (
    <>
      <Box
        mx={4}
        mt={2}
        mb={4}
        bg="white"
        borderRadius="16px"
        border="1px solid #EBEBEB"
        overflow="hidden"
        boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)">
        {/* Avatar + Name */}
        <VStack spacing={3} pt={6} pb={4}>
          <Box position="relative" onClick={onPhotoOpen} cursor="pointer">
            <UserAvatar user={user} w="80px" h="80px" fontSize="24px" />
            <Center
              position="absolute"
              bottom="0"
              right="0"
              w="28px"
              h="28px"
              bg="white"
              borderRadius="full"
              border="1px solid #E5E5E5"
              boxShadow="0 1px 3px rgba(0,0,0,0.1)">
              <i className="fi fi-rr-pencil" aria-hidden="true" style={{fontSize: '12px'}} />
            </Center>
          </Box>
          <TextG14 fontWeight="700" fontSize="18px" mt="0">
            {displayName}
          </TextG14>
        </VStack>

        <Divider borderColor="#F0F0F0" />

        {/* Nickname row */}
        <HStack
          as="button"
          w="100%"
          justify="space-between"
          px={5}
          py={4}
          onClick={onNicknameClick}
          _active={{bg: '#FAFAFA'}}>
          <TextG14 fontWeight="600" mt="0">
            Nickname
          </TextG14>
          <HStack spacing={1}>
            <TextG14 mt="0" color="#4D4D4D">
              {user?.handle ? `@${user.handle}` : 'Add'}
            </TextG14>
            <i
              className="fi fi-rr-angle-small-right"
              aria-hidden="true"
              style={{fontSize: '14px', color: theme.colors.grey}}
            />
          </HStack>
        </HStack>

        <Divider borderColor="#F0F0F0" mx={5} />

        {/* Home Address row */}
        <HStack
          as="button"
          w="100%"
          justify="space-between"
          px={5}
          py={4}
          onClick={onAddressClick}
          _active={{bg: '#FAFAFA'}}>
          <TextG14 fontWeight="600" mt="0">
            Home Address
          </TextG14>
          <HStack spacing={1}>
            <TextG14 mt="0" color="#4D4D4D" maxW="160px" noOfLines={1}>
              {displayAddress || 'Add'}
            </TextG14>
            <i
              className="fi fi-rr-angle-small-right"
              aria-hidden="true"
              style={{fontSize: '14px', color: theme.colors.grey}}
            />
          </HStack>
        </HStack>

        {/* Privacy notice */}
        {displayAddress && (
          <HStack px={5} pb={4} pt={1} spacing={2}>
            <Center w="40px" h="40px" bg="#D1F4D0" flexShrink={0} p="4px" borderRadius="8px">
              <Icon
                iconName="fi fi-sr-shield-check"
                style={{color: '#048400', fontSize: '26px', width: '26px', height: '26px'}}
              />
            </Center>
            <TextG14 mt="0" color="#262626" fontWeight="500">
              Your address is private! No one in the app will see it.
            </TextG14>
          </HStack>
        )}

        {/* My Connections button */}
        <Center pb={5} pt={2} px={5}>
          <HStack
            w="100%"
            justify="center"
            as="button"
            px={6}
            py={2.5}
            bg="#276ACD"
            color="white"
            borderRadius="24px"
            spacing={2}
            onClick={() => navigate('/my-profile/connections')}
            _hover={{bg: '#1F58AC'}}
            _active={{bg: '#1F58AC'}}>
            <i className="fi fi-rr-users" aria-hidden="true" style={{fontSize: '14px'}} />
            <TextG14 mt="0" fontWeight="600" color="white">
              My connections
            </TextG14>
          </HStack>
        </Center>
      </Box>

      {isPhotoOpen && (
        <ProfilePhotoModal
          show={isPhotoOpen}
          setShow={(val: boolean) => {
            if (!val) {
              onPhotoClose();
              reFetchUserData();
            }
          }}
        />
      )}
    </>
  );
};

export default ProfileCard;
